import React, { useState, useEffect } from 'react';
// NOTE: this page uses relative fetch paths, so no API_URL import is needed here.
import { useParams, Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaDownload, FaEye,
  FaFileAlt, FaGasPump
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ProformaPDFButton from '../components/ProformaPDFButton';
import { jobUnits, rateSuffix } from '../utils/jobUnits';

function CompletedJobDetail({ onLogout }) {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loads, setLoads] = useState([]);
  const [subbies, setSubbies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchJobDetail();
  }, [id]);

  const fetchJobDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch job details
      const jobRes = await fetch(`/jobs/${id}`);
      const jobData = await jobRes.json();
      if (jobData.success) {
        setJob(jobData.job);
      }

      // Fetch LOADS for this job. Loads live in `fleet_allocations` — the old `loads` table
      // is deprecated and always came back empty, which is why this page showed 0 loads,
      // 0 subcontractors and 0 drivers for EVERY completed job.
      const loadsRes = await fetch(`/fleet/fleet-allocations?job_id=${id}`);
      const loadsData = await loadsRes.json();
      const jobLoads = loadsData.allocations || loadsData.loads || [];
      setLoads(jobLoads);

      // Revenue: `estimated_value` is often empty (e.g. a lump-sum abnormal job), so fall
      // back to the total on the linked quotation.
      if (jobData.success && jobData.job && !jobData.job.estimated_value && jobData.job.quote_id) {
        try {
          const qRes = await fetch(`/quotes/${jobData.job.quote_id}`);
          const qData = await qRes.json();
          const grand = (qData && qData.quote && (qData.quote.grand_total || qData.quote.total_amount)) || null;
          if (grand) setJob((prev) => ({ ...prev, estimated_value: grand }));
        } catch { /* leave it as it was */ }
      }

      // Get subcontractors from loads
      const subbieIds = [...new Set(jobLoads.map(l => l.subcontractor_id).filter(Boolean) || [])];
      const subbieData = [];
      for (const sid of subbieIds) {
        const res = await fetch(`/subcontractors/${sid}`);
        const data = await res.json();
        if (data.success) {
          subbieData.push(data.subcontractor);
        }
      }
      setSubbies(subbieData);

      // Get drivers from loads
      const driverIds = [...new Set(jobLoads.map(l => l.driver_id).filter(Boolean) || [])];
      const driverData = [];
      for (const did of driverIds) {
        const res = await fetch(`/drivers/${did}`);
        const data = await res.json();
        if (data.success) {
          driverData.push(data.driver);
        }
      }
      setDrivers(driverData);

    } catch (err) {
      console.error('Error fetching job detail:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'loading': 'bg-blue-100 text-blue-600',
      'en_route': 'bg-yellow-100 text-yellow-600',
      'offloaded': 'bg-purple-100 text-purple-600',
      'completed': 'bg-green-100 text-green-600',
      'pending': 'bg-gray-100 text-gray-600',
      'cancelled': 'bg-red-100 text-red-600',
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status?.toLowerCase()] || styles.pending}`;
  };

  if (loading) {
    return (
      <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading job details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !job) {
    return (
      <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Job</h2>
          <p className="text-gray-500">{error || 'Job not found'}</p>
          <Link to="/completed-jobs" className="mt-4 inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            <FaArrowLeft className="inline mr-2" /> Back to Completed Jobs
          </Link>
        </div>
      </AdminLayout>
    );
  }

  // Calculate totals. NOTE the fleet_allocations column names: it is
  // weighbridge_weight_mt (not weight_mt) and actual_departure_at / actual_arrival_at
  // (not departure_time / arrival_time) — reading the old names always produced 0.
  const units = jobUnits(job);
  const weightOf = (l) => Number(l.weighbridge_weight_mt) || Number(l.pod_weight_mt) || 0;
  const totalTonnage = loads.reduce((sum, l) => sum + weightOf(l), 0);
  const totalLoads = loads.length;
  const completedLoads = loads.filter(l => ['completed', 'delivered'].includes(String(l.status || '').toLowerCase())).length;
  // TRAVEL TIME is only meaningful for loads a DRIVER actually ran (the driver app stamps
  // the real departure + arrival). An off-system "Record Completed Work" load has no
  // observed arrival at all — averaging the admin's start/end DATES produced nonsense like
  // "25.0 hours" for a 99 km trip no fleet was ever allocated to. So: null and the UI
  // explains, instead of inventing a number.
  const withTimes = loads.filter(l => l.actual_departure_at && l.actual_arrival_at);
  const avgTravelTime = withTimes.length
    ? withTimes.reduce((sum, l) => {
        const hours = (new Date(l.actual_arrival_at) - new Date(l.actual_departure_at)) / (1000 * 60 * 60);
        return sum + hours;
      }, 0) / withTimes.length
    : null;

  // Per-subcontractor revenue comes from the job's LINE ITEMS (client_price), because a
  // lump-sum job has no rate_per_mt to multiply tonnage by — that used to show R0 next to
  // an R87,400 job total. The allocations endpoint embeds job.line_items for us.
  // Falls back to tonnage × rate when the lines aren't embedded.
  const jobLines = (loads.find(l => l.job && l.job.line_items)?.job?.line_items) || [];

  // Calculate distance (using the job distance or average from loads)
  const totalDistance = job.distance_km || loads.reduce((sum, l) => sum + (l.distance_km || 0), 0);

  return (
    <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
      {/* Back button */}
      <Link to="/completed-jobs" className="inline-flex items-center gap-2 text-navy hover:text-green transition-colors mb-6">
        <FaArrowLeft size={16} /> Back to Completed Jobs
      </Link>

      {/* Job Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy">{job.job_number}</h1>
            <p className="text-gray-500">{job.client?.company_name || 'Unknown Client'}</p>
            <div className="flex flex-wrap gap-3 mt-2">
              <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm font-semibold">✅ COMPLETED</span>
              <span className="text-sm text-gray-500">Completed: {job.actual_end_date || job.estimated_end_date}</span>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {/* These two used to be dead buttons - no onClick at all. "Generate Invoice" now
                reuses the same idempotent proforma endpoint every other screen uses, and
                Export Report opens the browser's print/save-as-PDF dialog. */}
            <ProformaPDFButton invoice={{ job_id: id }} />
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors"
            >
              <FaDownload size={14} /> Export Report
            </button>
          </div>
        </div>

        {/* Job Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">{units.kind === 'mt' ? 'Total Tonnage' : 'Total Weight'}</p>
            <p className="text-2xl font-bold text-navy">{totalTonnage > 0 ? `${totalTonnage.toFixed(0)} MT` : '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Loads</p>
            <p className="text-2xl font-bold text-navy">{totalLoads}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-navy">{Number(job.estimated_value) > 0 ? `R${Number(job.estimated_value).toLocaleString()}` : '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Days Taken</p>
            <p className="text-2xl font-bold text-navy">
              {job.start_date && job.actual_end_date ? 
                Math.ceil((new Date(job.actual_end_date) - new Date(job.start_date)) / (1000 * 60 * 60 * 24)) : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Job Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Job Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">📋 Job Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Commodity</span>
              <span className="font-medium text-navy">{job.commodity}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Rate</span>
              <span className="font-medium text-navy">
                {Number(job.rate_per_mt) > 0
                  ? `R${Number(job.rate_per_mt).toLocaleString()} ${rateSuffix(units)}`.trim()
                  : (Number(job.estimated_value) > 0 ? `Lump sum — R${Number(job.estimated_value).toLocaleString()}` : '—')}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Origin</span>
              <span className="font-medium text-navy">{job.origin}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Destination</span>
              <span className="font-medium text-navy">{job.destination}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Distance</span>
              <span className="font-medium text-navy">{job.distance_km || 'N/A'} km</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Priority</span>
              <span className={`font-medium ${job.priority === 'urgent' ? 'text-red-600' : 'text-navy'}`}>
                {job.priority || 'Normal'}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">📊 Performance Metrics</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Average Travel Time</span>
              <span className="font-medium text-navy">{avgTravelTime === null ? '—' : `${avgTravelTime.toFixed(1)} hours`}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Total Distance</span>
              <span className="font-medium text-navy">{totalDistance} km</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Completion Rate</span>
              <span className="font-medium text-navy">{totalLoads > 0 ? ((completedLoads / totalLoads) * 100).toFixed(1) : 0}%</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Subcontractors Used</span>
              <span className="font-medium text-navy">{subbies.length}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Drivers Used</span>
              <span className="font-medium text-navy">{drivers.length}</span>
              {totalLoads > 0 && withTimes.length === 0 && (
                <p className="text-xs text-gray-400 pt-1">
                  Travel time is only measured on loads a driver actually ran (the driver app
                  stamps the real departure and arrival). These loads were recorded off-system,
                  so there is no arrival time to measure — that is why it shows “—”.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subcontractors Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-navy mb-4">🚛 Subcontractors Breakdown</h3>
        {subbies.length === 0 ? (
          <p className="text-gray-400 text-sm">No subcontractors found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Loads</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Travel Time</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {subbies.map((subbie) => {
                  const subbieLoads = loads.filter(l => l.subcontractor_id === subbie.id);
                  const subbieTonnage = subbieLoads.reduce((sum, l) => sum + weightOf(l), 0);
                  const subbieRevenue = jobLines.length
                    ? jobLines.filter(li => li.subcontractor_id === subbie.id).reduce((s, li) => s + (Number(li.client_price) || 0), 0)
                    : subbieLoads.reduce((sum, l) => sum + weightOf(l) * (Number(job.rate_per_mt) || 0), 0);
                  const subbieWithTimes = subbieLoads.filter(l => l.actual_departure_at && l.actual_arrival_at);
                  const subbieAvgTravel = subbieWithTimes.reduce((sum, l) => {
                    const hours = (new Date(l.actual_arrival_at) - new Date(l.actual_departure_at)) / (1000 * 60 * 60);
                    return sum + hours;
                  }, 0) / (subbieWithTimes.length || NaN); // NaN => the cell renders "—"
                  return (
                    <tr key={subbie.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-navy">{subbie.company_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{subbieLoads.length}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{subbieTonnage > 0 ? `${subbieTonnage.toFixed(0)} MT` : '—'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{Number.isFinite(subbieAvgTravel) ? `${subbieAvgTravel.toFixed(1)} hrs` : '—'}</td>
                      <td className="px-4 py-2 text-sm font-medium text-navy">R{subbieRevenue.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loads List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-navy mb-4">📦 Loads</h3>
        {loads.length === 0 ? (
          <p className="text-gray-400 text-sm">No loads found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Load #</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Commodity</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Weight</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loads.map((load) => (
                  <tr key={load.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-navy">{load.load_number}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{job.commodity || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{weightOf(load) > 0 ? `${weightOf(load)} MT` : '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{job.origin} → {job.destination}</td>
                    <td className="px-4 py-2">
                      <span className={getStatusBadge(load.status)}>{load.status || 'pending'}</span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button className="text-gray-400 hover:text-blue-600 transition-colors" title="View POD">
                          <FaFileAlt size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-green-600 transition-colors" title="View Fuel">
                          <FaGasPump size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-purple-600 transition-colors" title="View Details">
                          <FaEye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default CompletedJobDetail;
